import { useEffect, useMemo, useRef, useState } from 'react';
import useModalActions from '@hooks/common/useModalAction';
import StenosisReportModalContent, {
  StenosisReportData,
  StenosisRow,
} from './StenosisReportModalContent';
import { useConfig } from 'config/useConfig';
import { useReportModal } from '@hooks/useReportModal';
import {
  SurfaceContext,
  useBufferPool,
  VRContext,
} from '@features/analysis/rendering/Panels/Renderers/vtkManagers';
import { captureSurface3D, cropToSquare } from 'utils/captureUtils';
import BaseModalContent from '../BaseReportModalContent/BaseReportModalContetnt';
import { REPORT_MODAL_NAMES } from 'config/labels';
import { usePanelContainer } from '@features/analysis/rendering/Panels/usePanelContainer';
import { useAnalysisPage } from '@features/analysis/useAnalysisPage';
import { useBrowserPage } from '@features/browser/useBrowserPage';
import { BranchInfo } from 'utils/FunctionUtilites';
import { CalciumAnalysisResult } from '@models/Parameter';
import { setActorVisible } from '@features/view/Shared3DUtilities';
import { nodeNames } from 'config/render3DConfig';

export function parseReport(raw: string, imageUrl = ''): StenosisReportData {
  const lines = splitLines(raw);
  let calciumScore = 0;

  for (const s of lines) {
    const m = /calcium\s*scor(?:e|ing)\s*=\s*([0-9]+(?:\.[0-9]+)?)/i.exec(s);
    if (m) calciumScore = parseFloat(m[1]);
  }

  const rows: StenosisRow[] = [];
  for (const s of lines) {
    if (/calcium\s*scor(?:e|ing)/i.test(s)) continue;
    const row = parseOne(s);
    if (row) rows.push(row);
  }

  return { calciumScore, imageUrl, rows };
}

function splitLines(raw: string): string[] {
  return (raw || '')
    .replace(/\r\n?/g, '\n')
    .replace(/\\n/g, '\n')
    .split('\n')
    .map((x) => x.trim())
    .filter(Boolean);
}

function parseOne(s: string): StenosisRow | null {
  const m = /^\s*([A-Za-z0-9]+)\b(?:\s*[-_ ]\s*(proximal|mid|distal))?/i.exec(
    s,
  );
  if (!m) return null;

  const branch = m[1].toUpperCase();
  const seg = m[2] ? capitalize(m[2].toLowerCase()) : '';
  const vessel = seg ? `${branch} ${seg}` : branch;

  if (/unremarkable/i.test(s)) {
    return { vessel, percent: -1, plaque: 'unremarkable' };
  }

  const plaque = pickPlaque(s);
  const percent = toBucketMidPercent(s);
  return { vessel, percent, plaque };
}

function pickPlaque(s: string): string {
  const lower = s.toLowerCase();
  if (lower.includes('noncalcified')) return 'noncalcified plaque';
  if (lower.includes('calcified')) return 'calcified plaque';
  if (lower.includes('mixed')) return 'mixed plaque';
  if (lower.includes('no visible')) return 'no visible plaque';
  return '-';
}

function toBucketMidPercent(s: string): number {
  const lower = s.toLowerCase();

  if (/[≥>=]\s*70\s*%/.test(lower) || /70\s*[-–]\s*(?:99|100)\s*%/.test(lower))
    return 85;
  if (/50\s*[-–]\s*69\s*%/.test(lower)) return 59.5;
  if (/25\s*[-–]\s*49\s*%/.test(lower)) return 37;
  if (/1\s*[-–]\s*24\s*%/.test(lower)) return 12.5;
  if (/\b0\s*%/.test(lower)) return 0;

  const m = /(\d{1,3})\s*%/.exec(lower);
  if (m) return clamp01to100(parseInt(m[1], 10));
  return 0;
}

const clamp01to100 = (n: number) => Math.max(0, Math.min(100, n));
const capitalize = (s: string) => (s ? s[0].toUpperCase() + s.slice(1) : s);

const StenosisReportModal = ({ onClose }: { onClose: () => void }) => {
  const names = REPORT_MODAL_NAMES.stenosis;
  const { waitingAction, closeLoadingModal } = useModalActions();
  const { config } = useConfig();

  const { analysisType } = usePanelContainer();
  const { selectedLayoutConfig } = useAnalysisPage();
  const { selectedSeriesId } = useBrowserPage();

  // const [imageSrc, setImageSrc] = useState<{ threeD?: string }>();
  // const setter = async (type: string, imgPromise: Promise<string>) => {
  //   const img = await imgPromise;
  //   setImageSrc((prev) => ({
  //     ...prev,
  //     [type]: img,
  //   }));
  // };

  // useEffect(() => {
  //   if (imageSrc) {
  //     closeLoadingModal();
  //   } else if (!imageSrc) {
  //     waitingAction({ message: 'Waiting' });

  //     capture3d();

  //     // scheduleRender('Surface3D', {
  //     //   type: 'captureStenosis',
  //     //   key: 'threeD',
  //     //   setter,
  //     // });
  //   }
  // }, [imageSrc]);

  // const data = parseReport(config.stenosisResult, imageSrc?.threeD);

  const { bufferPool, contextPool } = useBufferPool();
  const poolRef = useRef(contextPool);
  useEffect(() => {
    poolRef.current = contextPool;
  }, [contextPool]);

  const [imgUrl, setImgUrl] = useState<string>('');
  async function capture3d(): Promise<string | undefined> {
    const keyname = selectedLayoutConfig.panelConfig.some(
      (panel) => panel.componentKey === 'Surface3D',
    )
      ? 'Surface3D'
      : 'VR';

    const d3context = {
      current:
        keyname === 'VR'
          ? (poolRef.current['VR'] as VRContext)
          : (poolRef.current['Surface3D'] as SurfaceContext),
    };
    if (!d3context) return;
    if (!d3context.current) return;
    waitingAction({ message: 'Waiting' });

    setActorVisible(d3context, false, [nodeNames.node, nodeNames.singleCenterLine]);
    let url = await captureSurface3D(d3context.current, analysisType,pool);
    url = await cropToSquare(url!);

    setImgUrl(url ?? '');
    setActorVisible(d3context, true, [nodeNames.node, nodeNames.singleCenterLine]);
    closeLoadingModal();
  }

  useEffect(() => {
    capture3d();
  }, []);

  const data = useMemo(
    () => parseReport(config.stenosisResult, imgUrl),
    [config.stenosisResult, imgUrl],
  );

  const pool = selectedSeriesId ? bufferPool[selectedSeriesId] : undefined;
  const dataset = useMemo(() => {
    const raw = pool?.branchGraphDataset?.data ?? null;
    return raw instanceof BranchInfo ? raw : null;
  }, [pool?.branchGraphDataset?.data]);

  const segmentRows = useMemo(() => {
    const defaultRows = [
      { vessel: 'LM', proximal: '', mid: '0', distal: '' },
      { vessel: 'LAD', proximal: '0', mid: '0', distal: '0' },
      { vessel: 'LCX', proximal: '0', mid: '-', distal: '0' },
      { vessel: 'RCA', proximal: '0', mid: '0', distal: '0' },
    ];

    const paramData = pool?.parameter.data as CalciumAnalysisResult | undefined;
    const lesioninfo =
      (config.stenosisLesionInfo as number[][][] | undefined) ?? undefined;
    const name = paramData?.SavedInfo?.Coronary?.newType;
    if (!dataset || !Array.isArray(name) || !Array.isArray(lesioninfo)) {
      return defaultRows;
    }

    const branchMap = new Map<string, number>();
    name.forEach((entry) => {
      const [branch, index] = entry as [string, number];
      if (typeof branch === 'string' && typeof index === 'number') {
        branchMap.set(branch, index);
      }
    });

    const formatPercent = (fraction?: number) => {
      if (typeof fraction !== 'number' || !Number.isFinite(fraction)) {
        return '0';
      }
      const percent = fraction > 1 ? fraction : fraction * 100;
      const rounded = Math.round(percent * 10) / 10;
      const fixed = rounded.toFixed(1);
      return fixed.endsWith('.0') ? fixed.slice(0, -2) : fixed;
    };

    const getTotalLength = (index: number) => {
      const arr = dataset.getLengthArray(index);
      if (!Array.isArray(arr) || arr.length === 0) return 0;
      return arr[arr.length - 1];
    };

    const buildRow = (branch: string) => {
      const index = branchMap.get(branch);
      const emptyRow =
        branch === 'LM'
          ? { vessel: 'LM', proximal: '', mid: '0', distal: '' }
          : branch === 'LCX'
            ? { vessel: 'LCX', proximal: '0', mid: '-', distal: '0' }
            : { vessel: branch, proximal: '0', mid: '0', distal: '0' };

      if (index === undefined || index < 0) return emptyRow;
      const lesions = Array.isArray(lesioninfo[index]) ? lesioninfo[index] : [];
      if (lesions.length === 0) return emptyRow;

      let maxFraction = 0;
      const bucket = { proximal: 0, mid: 0, distal: 0 };
      const totalLength = getTotalLength(index);

      lesions.forEach((lesion) => {
        const start = typeof lesion[0] === 'number' ? lesion[0] : 0;
        const end = typeof lesion[2] === 'number' ? lesion[2] : 0;
        const fraction = typeof lesion[3] === 'number' ? lesion[3] : 0;
        maxFraction = Math.max(maxFraction, fraction);

        if (branch === 'LM') return;
        if (totalLength <= 0) return;

        const center = Math.floor((start + end) * 0.5);
        const currentLength = dataset.getLengthValue(index, center);
        const length =
          typeof currentLength === 'number' && Number.isFinite(currentLength)
            ? currentLength
            : 0;
        let segment: 'proximal' | 'mid' | 'distal' = 'proximal';
        if (branch === 'LCX') {
          segment = length < totalLength / 2 ? 'proximal' : 'distal';
        } else {
          const third = totalLength / 3;
          if (length < third) segment = 'proximal';
          else if (length < third * 2) segment = 'mid';
          else segment = 'distal';
        }
        bucket[segment] = Math.max(bucket[segment], fraction);
      });

      if (branch === 'LM') {
        return {
          vessel: 'LM',
          proximal: '',
          mid: '0',
          distal: '',
        };
      }

      if (totalLength <= 0) {
        if (branch === 'LCX') {
          const value = formatPercent(maxFraction);
          return { vessel: 'LCX', proximal: value, mid: '-', distal: value };
        }
        const value = formatPercent(maxFraction);
        return { vessel: branch, proximal: value, mid: value, distal: value };
      }

      if (branch === 'LCX') {
        return {
          vessel: 'LCX',
          proximal: formatPercent(bucket.proximal),
          mid: '-',
          distal: formatPercent(bucket.distal),
        };
      }

      return {
        vessel: branch,
        proximal: formatPercent(bucket.proximal),
        mid: formatPercent(bucket.mid),
        distal: formatPercent(bucket.distal),
      };
    };

    return ['LM', 'LAD', 'LCX', 'RCA'].map(buildRow);
  }, [config.stenosisLesionInfo, dataset, pool?.parameter.data]);

  const ww = (1200 / 1920) * 100;
  const hh = (700 / 1080) * 100;

  return (
    <BaseModalContent
      className="stenosis"
      onClose={onClose}
      title={names.title}
      // vw={ww} vh={hh}
    >
      <StenosisReportModalContent
        onClose={onClose}
        data={data}
        segmentRows={segmentRows}
      />
    </BaseModalContent>
  );
};

export function useStenosisReportModal(res: {
  updateActivatedTool: (tool: any, check: boolean) => void;
}) {
  return useReportModal({ Component: StenosisReportModal, ...res });
}
